/**
 * DIE VERDRAHTUNG ZWISCHEN DEN BILDSCHIRMEN — aus App.tsx herausgelöst, damit sie getestet
 * werden kann (Prüfurteil ASSIOEFFNENR R2, P2-c: vier Mutationen in App.tsx überlebten,
 * weil App.tsx keinen Test hat). App.tsx reicht hier nur noch seine Setter herein.
 *
 *  - `b2AuftragAusfuehren`: was Bildschirm 2 mit einem Auftrag von Bildschirm 1 tut. Erst
 *    räumen (Mission Control nur, wenn dabei kein Text verloren geht), dann die Bühne setzen,
 *    dann an SEINEM DOM belegen, dass genau diese Bühne sichtbar steht — erst dann ok.
 *  - `vermerkAusQuittung`: Bildschirm 1 setzt den Vermerk `buehne-auf-b2` nur für die
 *    Quittung der Bühne, die gerade drüben stehen soll, und nur bei ok.
 *  - `fensterZusatz`: was ein Fenster der Fensterabfrage über Bühne und Vollbild meldet.
 */
import type { AuftragsNachricht } from './zweiterBildschirm';
import { rechtsAufBildschirm2, rechtsNichtGezeichnet, warteBisBelegt } from './guideOeffnungsBeleg';
import { ungesicherteEntwuerfe } from './gesicherterEntwurf';

type Bezug = Extract<AuftragsNachricht, { art: 'buehne' }>['bezug'];
export type Antwort = { ok: boolean; fehler?: string };

export interface B2Abhaengigkeiten {
  projektBekannt: (projectId: string) => boolean;
  /** null = geräumt; sonst der Grund, warum nicht (siehe App.tsx `abdeckungRaeumen`). */
  abdeckungRaeumen: () => string | null;
  buehneSetzen: (b: { doc: unknown; projectId: string; bezug?: Bezug } | null) => void;
  arbeitsbereichAktivieren: (projectId: string) => void;
  arbeitsbereichOeffnen: (projectId: string, panels?: string[]) => void;
  /** Der Beleg am eigenen DOM; Standard: `rechtsNichtGezeichnet` über `warteBisBelegt`. */
  belegen?: (buehneId: string) => Promise<string | null>;
}

const standardBeleg = (buehneId: string) => warteBisBelegt(() => rechtsNichtGezeichnet(document, buehneId), 2000);

export function b2AuftragAusfuehren(n: AuftragsNachricht, d: B2Abhaengigkeiten): Antwort | Promise<Antwort> {
  if (n.art === 'leeren') { d.buehneSetzen(null); return { ok: true }; }
  if (!d.projektBekannt(n.projectId)) {
    return { ok: false, fehler: `Arbeitsbereich „${n.projectId}" gibt es auf Bildschirm 2 nicht (noch nicht geladen?)` };
  }
  const nichtRaeumbar = d.abdeckungRaeumen();
  if (nichtRaeumbar) return { ok: false, fehler: nichtRaeumbar };
  if (n.art === 'buehne') {
    d.buehneSetzen({ doc: n.doc, projectId: n.projectId, bezug: n.bezug });
    d.arbeitsbereichAktivieren(n.projectId);
    const gezeigt = n.bezug?.id ?? 'vorschau';
    return (d.belegen ?? standardBeleg)(gezeigt)
      .then(fehlt => fehlt ? { ok: false, fehler: `Bühne auf Bildschirm 2 nicht gezeichnet: ${fehlt}` } : { ok: true });
  }
  d.arbeitsbereichOeffnen(n.projectId, n.panels);
  return { ok: true };
}

/** Vermerk für Bildschirm 1, oder null (keiner). `drueben`: was laut B1 gerade auf B2 stehen soll. */
export function vermerkAusQuittung(drueben: { doc: unknown } | null, n: AuftragsNachricht, ok: boolean): { bezugId?: string } | null {
  if (n.art !== 'buehne' || !ok || !drueben || drueben.doc !== n.doc) return null;
  return { bezugId: n.bezug?.id };
}

export function fensterZusatz(root: ParentNode, buehneId: string | null, buehneSichtbar: boolean, vollbild: boolean):
  { buehneAuf?: 'bildschirm1' | 'bildschirm2'; vollbild?: true } {
  return {
    ...(buehneSichtbar && buehneId ? { buehneAuf: rechtsAufBildschirm2(root, buehneId) ? 'bildschirm2' as const : 'bildschirm1' as const } : {}),
    ...(vollbild ? { vollbild: true as const } : {}),
  };
}

/**
 * Räumt Mission Control und „Alle Chats“ — außer Mission Control hält Text, der sich nicht
 * sichern ließ (`ungesicherteEntwuerfe`). null = geräumt; sonst der Grund (ASSIOEFFNENR P2-b).
 */
export function abdeckungRaeumenMit(missionOffen: boolean, ungesichert: string[], schliessen: () => void): string | null {
  if (missionOffen && ungesichert.length) {
    return `Mission Control enthält ungesendeten Text, der nicht in der Entwurfsablage steht (Speicher voll oder gesperrt; ${ungesichert.length} Feld${ungesichert.length === 1 ? '' : 'er'}); Mission Control bleibt offen, damit er nicht verloren geht.`;
  }
  schliessen();
  return null;
}

/** Was App.tsx aufruft: fragt die eingehängten Mission-Control-Felder selbst (ASSIOEFFNENR3 E3). */
export function missionAbdeckungRaeumen(missionOffen: boolean, schliessen: () => void): string | null {
  return abdeckungRaeumenMit(missionOffen, missionOffen ? ungesicherteEntwuerfe() : [], schliessen);
}
